export interface NavLink {
  label: string;
  path: string;
}

export interface NavSection {
  title: string;
  links: NavLink[];
}

/**
 * Navigation organized by learning flow:
 *
 * 1. Get Started     → What is this? How do I install it? Show me something working.
 * 2. Fundamentals    → The core primitives every user must know (signals, templates, mount).
 * 3. Building UI     → Styling, transitions, accessibility — making things look/feel good.
 * 4. Navigation      → Routing between pages.
 * 5. State & Data    → Managing state, fetching data, forms.
 * 6. Real-time       → WebSocket, SSE — live data.
 * 7. Advanced        → DI, security, performance, SSR — production concerns.
 * 8. Enterprise      → Microfrontends, plugins, observability — team/org-scale.
 * 9. Tooling         → CLI, DevTools, extensions, utilities.
 * 10. Playground     → Experiment freely.
 */
export const navSections: NavSection[] = [
  // ─── 1. ONBOARDING ───
  {
    title: 'Get Started',
    links: [
      { label: 'Introduction', path: '/' },
      { label: 'Installation', path: '/getting-started/install' },
      { label: 'Quick Start', path: '/getting-started/quickstart' },
    ],
  },

  // ─── 2. FUNDAMENTALS (must-know primitives) ───
  {
    title: 'Fundamentals',
    links: [
      { label: 'Signals (Reactivity)', path: '/core/signals' },
      { label: 'Templates (html)', path: '/core/templates' },
      { label: 'Mounting (mount)', path: '/core/mounting' },
      { label: 'Scoped CSS (css)', path: '/core/css' },
    ],
  },

  // ─── 3. BUILDING UI (visual & interaction) ───
  {
    title: 'UI & Styling',
    links: [
      { label: 'Theming', path: '/theming' },
      { label: 'Transitions', path: '/transitions' },
      { label: 'Accessibility', path: '/a11y' },
    ],
  },

  // ─── 4. NAVIGATION ───
  {
    title: 'Routing',
    links: [
      { label: 'Router', path: '/routing/router' },
      { label: 'configureRouter', path: '/routing/configure' },
      { label: 'Nested Routes', path: '/routing/nested' },
      { label: 'Dynamic Params', path: '/routing/params' },
      { label: 'Navigate', path: '/routing/navigate' },
      { label: 'Link', path: '/routing/link' },
    ],
  },

  // ─── 5. STATE & DATA ───
  {
    title: 'State Management',
    links: [
      { label: 'Store', path: '/state/store' },
      { label: 'Persisted Signals', path: '/state/persisted' },
      { label: 'Dependency Injection', path: '/di' },
    ],
  },
  {
    title: 'Data Fetching',
    links: [
      { label: 'Resource', path: '/data/resource' },
      { label: 'HTTP Client', path: '/data/http-client' },
      { label: 'Interceptors', path: '/data/interceptors' },
    ],
  },
  {
    title: 'Forms',
    links: [
      { label: 'createForm', path: '/forms/create-form' },
      { label: 'Validation Rules', path: '/forms/validation' },
    ],
  },

  // ─── 6. REAL-TIME ───
  {
    title: 'Real-time',
    links: [
      { label: 'WebSocket', path: '/streaming/websocket' },
      { label: 'Server-Sent Events', path: '/streaming/sse' },
    ],
  },

  // ─── 7. ADVANCED (production concerns) ───
  {
    title: 'Async Patterns',
    links: [
      { label: 'Suspense', path: '/async/suspense' },
      { label: 'Lazy Loading', path: '/async/lazy-loading' },
      { label: 'Error Boundaries', path: '/async/error-boundaries' },
    ],
  },
  {
    title: 'Security',
    links: [
      { label: 'XSS Prevention', path: '/security/xss' },
      { label: 'RBAC Guards', path: '/security/guards' },
    ],
  },
  {
    title: 'Performance',
    links: [
      { label: 'VirtualList', path: '/performance/virtual-list' },
      { label: 'Code Splitting', path: '/performance/code-splitting' },
    ],
  },
  {
    title: 'Server-Side Rendering',
    links: [{ label: 'renderHTML', path: '/ssr' }],
  },
  {
    title: 'Internationalization',
    links: [{ label: 'i18n', path: '/i18n' }],
  },

  // ─── 8. ENTERPRISE ───
  {
    title: 'Microfrontends',
    links: [
      { label: 'loadRemote', path: '/microfrontends/load-remote' },
      { label: 'Isolation Modes', path: '/microfrontends/isolation' },
      { label: 'Communication', path: '/microfrontends/communication' },
      { label: 'configureSecurity', path: '/microfrontends/security' },
      { label: 'SRI Integrity', path: '/microfrontends/sri' },
      { label: 'Shared Dependencies', path: '/microfrontends/shared-deps' },
      { label: 'Cross-Framework', path: '/microfrontends/cross-framework' },
      { label: 'Deployment', path: '/microfrontends/deployment' },
      { label: 'API Reference', path: '/microfrontends/api-reference' },
    ],
  },
  {
    title: 'Interop',
    links: [
      { label: 'wrapImperative', path: '/interop/wrap-imperative' },
      { label: 'embedForeign', path: '/interop/embed-foreign' },
    ],
  },
  {
    title: 'Plugins & Observability',
    links: [
      { label: 'Plugins', path: '/plugins' },
      { label: 'Observability', path: '/observability' },
      { label: 'Component Metadata', path: '/meta' },
    ],
  },

  // ─── 9. TOOLING ───
  {
    title: 'Tooling',
    links: [
      { label: 'CLI (create-onefold)', path: '/cli' },
      { label: 'DevTools', path: '/devtools' },
      { label: 'Extensions', path: '/extensions' },
      { label: 'Utilities', path: '/utilities' },
    ],
  },

  // ─── 10. DEPLOYMENT ───
  {
    title: 'Deployment',
    links: [
      { label: 'GitHub Pages', path: '/deployment/github-pages' },
      { label: 'Vercel', path: '/deployment/vercel' },
      { label: 'Netlify', path: '/deployment/netlify' },
      { label: 'Cloudflare Pages', path: '/deployment/cloudflare' },
      { label: 'AWS S3 + CloudFront', path: '/deployment/aws' },
      { label: 'Docker / Node', path: '/deployment/docker' },
    ],
  },

  // ─── 11. PLAYGROUND ───
  {
    title: 'Playground',
    links: [{ label: 'Live Editor', path: '/playground' }],
  },
];
